import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import useUser from '../../hooks/useUser';
import { getPageName } from '../../utilities/routeTitles';
import { submitIssueReport } from '../../utilities/api';
import AnnotationCanvas from './AnnotationCanvas';

const TOOLS = [
    { key: 'pen', icon: 'pi pi-pencil', label: 'Pen' },
    { key: 'arrow', icon: 'pi pi-arrow-up-right', label: 'Arrow' },
    { key: 'rect', icon: 'pi pi-stop', label: 'Rectangle' },
    { key: 'text', icon: 'pi pi-font', label: 'Text' }
];

const dataUrlToBlob = async (dataUrl) => {
    const res = await fetch(dataUrl);
    return res.blob();
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ReportIssueDialog({ visible, onHide, imageSrc, toast }) {
    const location = useLocation();
    const { loggedInUser, username, role, userRole, ubranch, ubranchid, clntid, userClient } = useUser();

    const [tool, setTool] = useState('pen');
    const [email, setEmail] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const canvasRef = useRef(null);

    const resetAndClose = () => {
        setEmail('');
        setDescription('');
        setTool('pen');
        onHide();
    };

    const handleSubmit = async () => {
        if (!email.trim() || !EMAIL_PATTERN.test(email.trim())) {
            toast?.current?.show({ severity: 'warn', summary: 'Valid Email Required', detail: 'Please enter a valid email address so we can reply with an update.' });
            return;
        }
        if (!description.trim()) {
            toast?.current?.show({ severity: 'warn', summary: 'Description Required', detail: 'Please describe the issue before submitting.' });
            return;
        }

        setSubmitting(true);
        try {
            const flattenedDataUrl = canvasRef.current?.getDataUrl();
            if (!flattenedDataUrl) throw new Error('Could not capture the annotated screenshot');
            const blob = await dataUrlToBlob(flattenedDataUrl);

            const pageName = getPageName(location.pathname);
            const screenResolution = `${window.screen.width}x${window.screen.height}`;

            const formData = new FormData();
            formData.append('screenshot', blob, 'screenshot.png');
            formData.append('reporter_email', email.trim());
            formData.append('description', description.trim());
            formData.append('page_name', pageName);
            formData.append('page_path', location.pathname + (location.hash || ''));
            formData.append('reported_by_userid', loggedInUser || '');
            formData.append('reported_by_username', username || '');
            formData.append('reported_by_role', userRole || role || '');
            formData.append('branch_id', ubranchid || '');
            formData.append('branch_name', ubranch || '');
            formData.append('client_id', userClient || clntid || '');
            formData.append('reported_at', new Date().toISOString());
            formData.append('user_agent', window.navigator.userAgent);
            formData.append('screen_resolution', screenResolution);

            await submitIssueReport(formData);

            toast?.current?.show({ severity: 'success', summary: 'Report Sent', detail: 'Your issue has been reported to the support team.' });
            resetAndClose();
        } catch (error) {
            toast?.current?.show({ severity: 'error', summary: 'Submit Failed', detail: error?.response?.data?.message || 'Could not send the report. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            header={
                <div className="flex align-items-center gap-3" style={{ width: '100%' }}>
                    <span className="text-lg font-semibold white-space-nowrap">Report an Issue</span>
                    <div className="flex flex-wrap justify-content-center gap-2" style={{ flex: 1 }}>
                        {TOOLS.map((t) => (
                            <Button
                                key={t.key}
                                icon={t.icon}
                                label={t.label}
                                className={`p-button-sm ${tool === t.key ? '' : 'p-button-outlined'}`}
                                onClick={() => setTool(t.key)}
                            />
                        ))}
                        <Button icon="pi pi-undo" label="Undo" className="p-button-sm p-button-secondary p-button-outlined" onClick={() => canvasRef.current?.undo()} />
                        <Button icon="pi pi-trash" label="Clear" className="p-button-sm p-button-danger p-button-outlined" onClick={() => canvasRef.current?.clear()} />
                    </div>
                </div>
            }
            visible={visible}
            onHide={resetAndClose}
            style={{ width: '95vw', maxWidth: '950px', height: '88vh' }}
            breakpoints={{ '640px': '98vw' }}
            modal
            dismissableMask
            contentStyle={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
            footer={
                <div className="flex flex-column gap-2" style={{ width: '100%' }}>
                    <div className="flex align-items-center gap-2">
                        <label className="font-medium white-space-nowrap" style={{ fontSize: '0.85rem', minWidth: 82 }}>
                            Your Email <span style={{ color: '#e53935' }}>*</span>
                        </label>
                        <InputText
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full"
                            style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
                            placeholder="you@example.com — we'll reply here once it's resolved"
                        />
                    </div>
                    <div className="flex align-items-start gap-2">
                        <label className="font-medium white-space-nowrap" style={{ paddingTop: 6, fontSize: '0.85rem', minWidth: 82 }}>
                            Description <span style={{ color: '#e53935' }}>*</span>
                        </label>
                        <InputTextarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={1}
                            autoResize
                            className="w-full"
                            style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
                            placeholder="What went wrong?"
                        />
                    </div>
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm" onClick={resetAndClose} disabled={submitting} />
                        <Button label="Submit" icon="pi pi-send" className="p-button-sm" onClick={handleSubmit} loading={submitting} />
                    </div>
                </div>
            }
        >
            <AnnotationCanvas ref={canvasRef} imageSrc={imageSrc} tool={tool} />
        </Dialog>
    );
}
